import { AfterViewInit, ChangeDetectorRef, Directive, ViewChild, ViewContainerRef } from "@angular/core";
import { DataResponse, Deck, DeckCard, DeckCardAddition, DeckCardChanges, DeckLayer, GameType, UpdateDeckRequest, UpdateDeckResponse } from "@apiserver-rest/ponder-data-client-base";
import { environment } from "../../../../../environments/environment";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { debounceTime, Subscription, take } from "rxjs";
import { WindowResizeEvent } from "@ponder-core/events/ui.events";
import { getScreenBreakpoint, isMobileScreen, ScreenBreakpoint } from "@ponder-shared/utils/screenBreakpoints";
import { DeckLayerName, PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { DeckLayerAddCardActionType, DecklayerAddCardResult, DeckLayerComponent } from "./deck-layer.component";
import { AuthService } from "@ponder-core/auth/auth.service";
import { FilteredDeckPreviewPreviewItems } from "@ponder-shared/tools/filter/filtered-deck-preview-items";
import { PreviewItemFilters } from "@ponder-shared/tools/filter/preview-item-filters";
import { formatDate } from "@ponder-shared/utils/general";
import { AuthSession } from "@ponder-core/auth/auth-session";
import { NavigationService } from "@ponder-core/navigation/navigation.service";

@Directive()
export class DeckBaseComponent implements AfterViewInit {
    @ViewChild("deckLayers", { read: ViewContainerRef }) protected deckLayersVcr!: ViewContainerRef;
    protected deckLayers: Map<DeckLayerName, DeckLayerComponent> = new Map<DeckLayerName, DeckLayerComponent>();

    public username?: string;
    public isOwnDeck: boolean = false;
    public isAuthed: boolean = false;

    public deck!: Deck;
    public filteredItems!: FilteredDeckPreviewPreviewItems;
    public focusedCard?: DeckCard;
    public currentLayer!: DeckLayer;
    public mainLayer!: DeckLayer;
    public currentLayerComponent!: DeckLayerComponent;
    public screenSize!: ScreenBreakpoint;

    public createdAt?: string;
    public updatedAt?: string;

    private resizeEventSub!: Subscription;
    private filterChangeSub!: Subscription;
    private cardVersionSwitchedSub!: Subscription;
    private deckDeletedSub!: Subscription;

    public constructor(
        public game: GameType, 
        protected events: EventBroker, 
        protected ponderDataClient: PonderDataClient,
        protected auth: AuthService,
        public cdr: ChangeDetectorRef,
        private navigation: NavigationService) {

        this.screenSize = getScreenBreakpoint();
        this.events.ui.removeContentPaddingEvent.emit();

        this.resizeEventSub = this.events.ui.windowResizeEvent.subscribe({
            next: (_: WindowResizeEvent) => {
                this.onScreenResize();
            }
        });

        this.filterChangeSub = this.events.ui.previewFilterChangeEvent
            .pipe(debounceTime(100))
            .subscribe({
                next: (filter: PreviewItemFilters) => {
                    this.onFilterUpdated(filter);
                }
            });

        this.cardVersionSwitchedSub = this.events.deck.onCardVersionSwitchedEvent
            .subscribe({
                next: (deckCard: DeckCard) => {
                    // remove old one too?
                    this.filteredItems.addItem(deckCard.card);
                    this.filteredItems.rebuildCardMap();
                    this.filteredItems.populate();
                }
            });

        this.deckDeletedSub = this.events.deck.deckDeletedEvent
            .subscribe({
                next: (deckId: string) => {
                    if (deckId == this.deck.id) {
                        if (this.username) {
                            this.navigation.goto(`/user/${this.username}/decks`);
                        } else {
                            this.navigation.goto("/");
                        }
                    }
                }
            });
    }
    
    public initialize(deck: Deck, deckLayer: DeckLayer): void {
        this.deck = deck;
        this.currentLayer = deckLayer;
        this.mainLayer = deck.layers![0];
        this.createdAt = formatDate(this.deck.createdAt);
        this.updatedAt = formatDate(this.deck.updatedAt);
        
        this.initializeFilters();
    }
    
    // -------------------------------------------------------------------------------------
    // Life Cycle
    // -------------------------------------------------------------------------------------

    public ngOnDestroy() {
        this.events.ui.restoreContentPaddingEvent.emit();
        this.resizeEventSub?.unsubscribe();
        this.filterChangeSub?.unsubscribe();
        this.cardVersionSwitchedSub?.unsubscribe();
        this.deckDeletedSub?.unsubscribe();
    }

    public ngOnInit(): void {
        this.auth.getSessionAsync().then((authSession?: AuthSession) => {
            if (authSession?.username) {
                this.isAuthed = true;
                this.isOwnDeck = authSession.username == this.deck.owner;
                this.username = authSession.username;

                this.onAuthed();
            }
        });

        this.setInitialFocusedCard();

        // capitalize the layer names. not sure atm why this is coming over lowercase, its stored properly in the database ...
        // ... note to self its probably not the db but the layer type enum ...
        this.deck.layers?.forEach(l => l.name = l.name!.charAt(0).toUpperCase() + l.name!.slice(1));
    }

    public ngAfterViewInit(): void {
        // ... implement in child to initialize layers, etc ...
    }

    protected onAuthed(): void {

    }
    
    // -------------------------------------------------------------------------------------
    // Filters
    // -------------------------------------------------------------------------------------

    private initializeFilters(): void {
        this.filteredItems = new FilteredDeckPreviewPreviewItems(this.deck, this.events);
        this.filteredItems.populate();

        this.events.ui.toggleSortFilterBarEvent.emit(true);
        this.events.ui.previewItemsFetchedEvent.emit(this.filteredItems);
    }

    private onFilterUpdated(filter: PreviewItemFilters): void {
        this.filteredItems?.populate(filter);
        //this.cdr.detectChanges();
    }

    public isCardVisible(deckCard: DeckCard): boolean {
        return this.filteredItems.isCardVisible(deckCard);
    }

    // -------------------------------------------------------------------------------------
    // Focused Card
    // -------------------------------------------------------------------------------------

    private setInitialFocusedCard(): void {
        var initialFocusedCardId = this.getInitialFocusedCardId();
        var matchingCard = this.getCard(initialFocusedCardId ?? this.deck.backgroundCardId);

        if (matchingCard) {
            this.focusedCard = matchingCard;
        }
        else if (this.currentLayer.cards?.length) {
            this.focusedCard = this.currentLayer.cards[0];
        }
    }

    public getFocusedImageUrl(): string {
        if (this.focusedCard) {
            return `${environment.ponderImageBaseUrl}/card/large/front/${this.focusedCard.card.item.id!}.jpg`;
        } else {
            // ... todo what to do with new deck? ...
            return '';
        }
    }
    
    // -------------------------------------------------------------------------------------
    // Card Actions
    // -------------------------------------------------------------------------------------

    public getCard(id: string | undefined): DeckCard | undefined {
        if (!id || !this.deck.layers) {
            return undefined;
        }

        for (let i = 0; i < this.deck.layers.length; ++i) {
            var layer = this.deck.layers![i];

            if (layer.cards) {
                for (let j = 0; j < layer.cards.length; ++j) {
                    var card = layer.cards[j];

                    if (card.card.item.id! == id) {
                        return card;
                    }
                }
            }
        }

        return undefined;
    }

    public getCardActiveLayer(id: string | undefined): DeckCard | undefined {
        if (!id || !this.currentLayer || !this.currentLayer.cards) {
            return undefined;
        }

        for (let i = 0; i < this.currentLayer.cards.length; ++i) {
            if (this.currentLayer.cards[i].card.item.id === id) {
                return this.currentLayer.cards[i];
            }
        }

        return undefined;
    }

    private processingCardCount: boolean = false;

    public adjustCardCount(
        deckCard: DeckCard, 
        delta: number, 
        specialDelta: number,
        onComplete?: (success: boolean) => void): void {
            
        if (this.processingCardCount) {
            return;
        }

        this.processingCardCount = true;

        var activeLayerComponent = this.currentLayerComponent;
        var oldCount = deckCard.count;
        var newCount = Math.max(0, Math.min(oldCount + delta, 999));
        
        var oldSpecialCount = deckCard.specialCount;
        var newSpecialCount = Math.max(0, Math.min(oldSpecialCount + specialDelta, 999));

        deckCard.count = newCount;
        deckCard.specialCount = newSpecialCount;

        var request = new UpdateDeckRequest({
            deckId: this.deck.id,
            cardChanges: [
                new DeckCardChanges({
                    cardId: deckCard.deckCardId,
                    count: newCount,
                    specialCount: newSpecialCount
                })
            ]
        });

        var success = false;

        this.ponderDataClient
            .deckUpdate(request)
            .pipe(take(1))
            .subscribe({
                next: (response: DataResponse) => {
                    if (response.success) {
                        success = true;
                        this.events.deck.deckCardEvent.emit({
                            deckCard: deckCard,
                            prevCount: oldCount,
                            currCount: newCount,
                            countChange: (newCount - oldCount),
                            prevSpecialCount: oldSpecialCount,
                            currSpecialCount: newSpecialCount,
                            specialCountChange: (newSpecialCount - oldSpecialCount),
                            layer: activeLayerComponent.name
                        });
                    } else {
                        deckCard.count = oldCount;
                        deckCard.specialCount = oldSpecialCount;
                    }

                },
                error: () => {
                    deckCard.count = oldCount;
                    deckCard.specialCount = oldSpecialCount;
                }
            })
            .add(() => {
                onComplete?.(success);
                this.processingCardCount = false;
            });
    }

    public reloadCard(card: DeckCard, layerName: DeckLayerName): void {
        var layer = this.deckLayers.get(layerName);

        if (layer) {
            layer.removeCard(card);
            layer.addCard(card);
        }
    }

    // -------------------------------------------------------------------------------------
    // Layer Actions
    // -------------------------------------------------------------------------------------

    public onLayerClick(selectedLayer: DeckLayer): void {
        this.onMobileLayerSelect(selectedLayer.name!);
    }

    public onMobileLayerSelect(layerName: string): void {
        if (this.currentLayer.name === layerName) {
            return;
        }

        this.deckLayers.forEach((layer: DeckLayerComponent, _, __) => {
            if (layer.name === layerName) {
                this.currentLayer = layer.deckLayer;
                this.currentLayerComponent = layer;
                layer.isVisible = true;
            } else {
                layer.isVisible = false;
            }
        });
    }

    public moveCardToLayer(card: DeckCard, from: DeckLayerName, to: DeckLayerName): void {
        if (from == to) {
            return;
        }

        var fromLayer =  this.deckLayers.get(from);
        var toLayer = this.deckLayers.get(to);

        if (!fromLayer || !toLayer) {
            return;
        }
        
        fromLayer.removeCard(card);
        var addResult: DecklayerAddCardResult = toLayer.addCard(card);

        var request = new UpdateDeckRequest({
            deckId: this.deck.id,
            cardChanges: []
        });

        if (addResult.action == DeckLayerAddCardActionType.Added) {
            request.cardChanges?.push(new DeckCardChanges({
                cardId: card.deckCardId,
                layer: to
            }));
        } else if (addResult.action == DeckLayerAddCardActionType.Updated) {
            // If the card already exists on the layer, we want to update that existing card with the added counts
            request.cardChanges!.push(new DeckCardChanges({
                cardId: addResult.deckCard.deckCardId,
                count: addResult.deckCard.count,
                specialCount: addResult.deckCard.specialCount
            }));
            // And then remove the card from the old layer.
            request.cardChanges!.push(new DeckCardChanges({
                cardId: card.deckCardId,
                count: -card.count,
                specialCount: -card.specialCount
            }));
        }

        this.ponderDataClient
            .deckUpdate(request)
            .pipe(take(1))
            .subscribe({
                next: (response: DataResponse) => {
                    if (!response.success) {
                        toLayer!.removeCard(card);
                        fromLayer!.addCard(card);
                    }
                },
                error: (_: any) => {
                    toLayer!.removeCard(card);
                    fromLayer!.addCard(card);
                }
            })
            .add(() => {
                toLayer!.recalculateTotals();
                fromLayer!.recalculateTotals();
            });
    }

    public addCardToActiveLayer(cardName: string, cardId: string, matchOnName: boolean, normalDelta: number, specialDelta: number): void {
        if (!this.currentLayer?.name) {
            return;
        }

        var activeLayerComponent = this.currentLayerComponent;
        var preexistingCard = (matchOnName ? activeLayerComponent.getCard(cardName, undefined) : activeLayerComponent.getCard(undefined, cardId));

        // UPDATE
        if (preexistingCard) {
            // When blindly adding a card to the deck, add only the non-foil version
            var oldCount = preexistingCard.count;
            var newCount = oldCount + normalDelta;

            var oldSpecialCount = preexistingCard.specialCount;
            var newSpecialCount = oldSpecialCount + specialDelta;

            preexistingCard.count += (normalDelta + specialDelta);
            
            var updateRequest = new UpdateDeckRequest({
                deckId: this.deck.id,
                cardChanges: [
                    new DeckCardChanges({
                        cardId: preexistingCard.deckCardId!,
                        count: newCount,
                        specialCount: newSpecialCount
                    })
                ]
            });

            this.ponderDataClient
                .deckUpdate(updateRequest)
                .pipe(take(1))
                .subscribe({
                    next: (response: UpdateDeckResponse) => {
                        if (response.success) {
                            this.events.deck.deckCardEvent.emit({
                                deckCard: preexistingCard!,
                                prevCount: oldCount,
                                currCount: newCount,
                                countChange: normalDelta,
                                prevSpecialCount: preexistingCard!.specialCount,
                                currSpecialCount: preexistingCard!.specialCount,
                                specialCountChange: specialDelta,
                                layer: activeLayerComponent.name
                            });
                        } else {
                            preexistingCard!.count = oldCount;
                            preexistingCard!.specialCount = oldSpecialCount;
                        }
                    },
                    error: (_) => {
                        preexistingCard!.count = oldCount;
                        preexistingCard!.specialCount = oldSpecialCount;
                    }
                });

        } 
        // INSERT
        else {
            var updateRequest = new UpdateDeckRequest({
                deckId: this.deck.id,
                cardAdditions: [
                    new DeckCardAddition({
                        cardId: cardId,
                        count: normalDelta,
                        specialCount: specialDelta,
                        layer: this.currentLayer.name
                    })
                ]
            });

            this.ponderDataClient
                .deckUpdate(updateRequest)
                .pipe(take(1))
                .subscribe({
                    next: (response: UpdateDeckResponse) => {
                        if (response.success && response.cardAdditions) {
                            this.filteredItems.addItem(response.cardAdditions[0].deckCard.card);
                            this.filteredItems.populate();
                            
                            this.events.deck.deckCardEvent.emit({
                                deckCard: response.cardAdditions[0].deckCard,
                                prevCount: 0,
                                currCount: normalDelta,
                                countChange: normalDelta,
                                prevSpecialCount: 0,
                                currSpecialCount: specialDelta,
                                specialCountChange: specialDelta,
                                layer: activeLayerComponent.name
                            });
                        }
                    }
                });
        }
    }
    
    // -------------------------------------------------------------------------------------
    // Misc Actions
    // -------------------------------------------------------------------------------------

    public onEditDeckClicked(): void {

    }

    // -------------------------------------------------------------------------------------
    // Data
    // -------------------------------------------------------------------------------------

    protected getInitialFocusedCardId(): string | undefined {
        return undefined;
    }

    public getSelf(): DeckBaseComponent {
        return this;
    }

    public onCardHover(deckCard: DeckCard): void {
        this.focusedCard = deckCard;
    }

    public isActiveLayer(layer: DeckLayer): boolean {
        return (layer == this.currentLayer);
    }

    protected onScreenResize(): void {
        this.screenSize = getScreenBreakpoint();
    }

    public isMobileScreen(): boolean {
        return isMobileScreen();
    }
}